import React, { useState } from "react";
import { Phone, Mail, Globe, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { waLink, formatApiErrorDetail } from "@/lib/api";
import { SectionHeading } from "@/components/SectionHeading";
import { useContent } from "@/lib/useContent";
import { usePageMeta } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

const SERVICES = ["UAE Visa Assistance", "Other Visa Assistance", "Flight Booking", "Hotel Reservation", "Holiday Package", "Tours & Excursions", "Airport Transfer", "Travel Insurance", "Corporate Travel", "Other"];

export default function Contact() {
  usePageMeta("Contact Us", "Get in touch with our offices in the UAE, Zimbabwe and South Africa — enquiries answered fast on WhatsApp, phone and email.");
  const PHONES = useContent("phones");
  const OFFICES = useContent("offices");
  const [f, setF] = useState({ full_name: "", phone: "", email: "", current_location: "", service_needed: "", travel_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", f);
      trackEvent("enquiry_submitted", { form: "contact" });
      toast.success("Message sent! A consultant will reply shortly.");
      setF({ full_name: "", phone: "", email: "", current_location: "", service_needed: "", travel_date: "", notes: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cls = "w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A62A]";
  const lbl = "text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5 block";

  return (
    <div className="bg-white">
      <section className="relative py-24 bg-[#0A2E4D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="mk-gold-line mb-6 inline-block" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#E3BA45] font-bold mb-3">Global Contact Centre</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-white">Let's plan your journey</h1>
          <p className="text-slate-300 mt-4 max-w-xl mx-auto">Three global offices, one WhatsApp away. We reply fast — usually within the hour during business time.</p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-14">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="font-serif-display text-2xl text-[#0A2E4D] mb-5">Call or WhatsApp</h2>
              <ul className="space-y-4">
                {PHONES.map((p) => (
                  <li key={p.tel} className="flex items-center justify-between border border-[#E5E7EB] rounded-xl p-4">
                    <a href={`tel:${p.tel}`} className="flex items-center gap-3 text-sm font-semibold text-[#0A2E4D]" data-testid={`contact-phone-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <span className="w-9 h-9 rounded-full bg-[#0A2E4D] flex items-center justify-center"><Phone size={14} className="text-[#E3BA45]" /></span>
                      <span>{p.number}<span className="block text-xs text-[#4B5563] font-normal">{p.flag ? `${p.flag} ` : ""}{p.label}</span></span>
                    </a>
                    <a href={`https://wa.me/${p.tel.replace("+", "")}`} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${p.label}`}
                       className="w-9 h-9 rounded-full bg-[#25D366]/10 text-[#25D366] flex items-center justify-center hover:bg-[#25D366] hover:text-white transition-colors"><MessageCircle size={16} /></a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-3 text-sm">
              <a href="mailto:explore@makchester.com" className="flex items-center gap-3 text-[#0A2E4D] font-semibold hover:text-[#D4A62A] transition-colors"><Mail size={16} className="text-[#D4A62A]" /> explore@makchester.com</a>
              <div className="flex items-center gap-3 text-[#4B5563]"><Globe size={16} className="text-[#D4A62A]" /> makchester.com</div>
            </div>
            <div>
              <h3 className="font-serif-display text-xl text-[#0A2E4D] mb-4">Global Offices</h3>
              <ul className="space-y-3 text-sm text-[#4B5563]">
                {OFFICES.map((o) => (
                  <li key={o.country} className="flex items-start gap-3" data-testid={`office-${o.country.toLowerCase().replace(/\s+/g, "-")}`}>
                    <MapPin size={16} className="text-[#D4A62A] mt-0.5 shrink-0" />
                    <span><strong className="text-[#0A2E4D]">{o.flag ? `${o.flag} ` : ""}{o.country}</strong> <span className="text-xs text-[#9CA3AF]">({o.label})</span><br />{o.address}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#E5E7EB] h-64">
              <iframe title="Makchester Harare Office" src="https://www.google.com/maps?q=Gelfand+House,+Speke+Avenue,+Harare,+Zimbabwe&output=embed"
                className="w-full h-full border-0" loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" data-testid="contact-map" />
            </div>
          </div>

          <div className="lg:col-span-3">
            <SectionHeading center={false} eyebrow="Send an Enquiry" title="Tell us what you need" />
            <form onSubmit={submit} className="mk-card !transform-none p-8 grid grid-cols-2 gap-4 -mt-6" data-testid="contact-form">
              <div><label className={lbl}>Full Name *</label><input required value={f.full_name} onChange={set("full_name")} className={cls} data-testid="contact-name-input" /></div>
              <div><label className={lbl}>Phone / WhatsApp *</label><input required value={f.phone} onChange={set("phone")} className={cls} data-testid="contact-phone-input" /></div>
              <div><label className={lbl}>Email</label><input type="email" value={f.email} onChange={set("email")} className={cls} data-testid="contact-email-input" /></div>
              <div><label className={lbl}>Current Location</label><input value={f.current_location} onChange={set("current_location")} className={cls} placeholder="e.g. Harare" /></div>
              <div><label className={lbl}>Service Needed *</label>
                <select required value={f.service_needed} onChange={set("service_needed")} className={cls} data-testid="contact-service-select">
                  <option value="">Select a service…</option>
                  {SERVICES.map((s) => <option key={s}>{s}</option>)}
                </select></div>
              <div><label className={lbl}>Intended Travel Date</label><input type="date" value={f.travel_date} onChange={set("travel_date")} className={cls} /></div>
              <div className="col-span-2"><label className={lbl}>Message</label><textarea rows={5} value={f.notes} onChange={set("notes")} className={cls} placeholder="Tell us about your plans…" data-testid="contact-message-input" /></div>
              <button type="submit" disabled={busy} className="mk-btn-gold col-span-2 !py-4 disabled:opacity-60" data-testid="contact-submit-btn">{busy ? "Sending…" : "Send Enquiry"}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
